import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { BrandId } from '@hub/shared'
import { request } from './api.js'
import type { ItemDetail, NewItem } from './items.js'

export type InspirationStatus = 'neu' | 'gemerkt' | 'verworfen' | 'umgesetzt'
export type InspirationSource = 'anlass' | 'link' | 'claude' | 'instagram' | 'pinterest' | 'meta_ads' | 'trends' | 'performance'

export interface Inspiration {
  id: string
  source: InspirationSource
  brand: BrandId | null
  title: string
  summary: string
  url: string | null
  imageUrl: string | null
  tags: string[]
  score: number | null
  date: string | null
  status: InspirationStatus
  itemId: string | null
  createdBy: 'lucas' | 'claude'
  foundAt: string
}

export const SOURCE_LABEL: Record<InspirationSource, string> = {
  anlass: 'Anlass',
  link: 'Gemerkter Link',
  claude: 'Recherche von Claude',
  instagram: 'Instagram',
  pinterest: 'Pinterest',
  meta_ads: 'Meta Ad Library',
  trends: 'Trend',
  performance: 'Eigene Performance',
}

export function useInspiration(status: InspirationStatus[], brand: string[]) {
  const params = new URLSearchParams({ status: status.join(',') })
  if (brand.length) params.set('brand', brand.join(','))
  return useQuery({
    queryKey: ['inspiration', status, brand],
    queryFn: () => request<{ items: Inspiration[] }>(`/inspiration?${params}`).then((r) => r.items),
    placeholderData: (previous) => previous,
  })
}

function useRefresh() {
  const client = useQueryClient()
  return () => void client.invalidateQueries({ queryKey: ['inspiration'] })
}

export function useSaveLink() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (body: { url: string; brand?: BrandId | null; note?: string }) =>
      request<Inspiration>('/inspiration/link', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: refresh,
  })
}

export function useSetInspiration() {
  const client = useQueryClient()
  const refresh = useRefresh()
  return useMutation({
    mutationFn: ({ id, status, brand }: { id: string; status: InspirationStatus; brand?: BrandId | null }) =>
      request<Inspiration>(`/inspiration/${id}`, { method: 'PATCH', body: JSON.stringify({ status, ...(brand !== undefined ? { brand } : {}) }) }),
    // Dismissing should feel instant: the card leaves the list before the server answers.
    onMutate: ({ id, status }) => {
      for (const [key, data] of client.getQueriesData<Inspiration[]>({ queryKey: ['inspiration'] })) {
        const shown = (key[1] as InspirationStatus[] | undefined) ?? []
        if (data && !shown.includes(status)) client.setQueryData(key, data.filter((x) => x.id !== id))
      }
    },
    onSettled: refresh,
  })
}

export function useConvertInspiration() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, item }: { id: string; item: NewItem }) =>
      request<ItemDetail>(`/inspiration/${id}/convert`, { method: 'POST', body: JSON.stringify(item) }),
    onSuccess: () => {
      for (const key of ['inspiration', 'items']) void client.invalidateQueries({ queryKey: [key] })
    },
  })
}

export function useDeleteInspiration() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => request<{ ok: true }>(`/inspiration/${id}`, { method: 'DELETE' }),
    onMutate: (id) => {
      for (const [key, data] of client.getQueriesData<Inspiration[]>({ queryKey: ['inspiration'] })) {
        if (data) client.setQueryData(key, data.filter((x) => x.id !== id))
      }
    },
    onSettled: () => void client.invalidateQueries({ queryKey: ['inspiration'] }),
  })
}
